import type { SignupPath } from "@/lib/auth/session-types";
import type { OnboardingIntent } from "@/lib/onboarding/onboarding-intent";
import type { SignupStep } from "@/lib/onboarding/onboarding-types";

/** Where the person is in the onboarding: the CPF, the code of a known CPF, or a signup step. */
export type OnboardingProgressStep = "cpf" | "login" | SignupStep;

/** The list's own steps, after the account ones, as the prototype counts them (`authFlow`). */
const LIST_STEPS = ["evento", "termos", "partida"] as const;
type ListStep = (typeof LIST_STEPS)[number];

/**
 * The account part of each path. The app asks the address before the code for a new person
 * (the register call carries it), and after the code for a known CPF that still owes one.
 */
function accountSteps(path: SignupPath): OnboardingProgressStep[] {
  if (path === "new") return ["cpf", "signup", "address", "confirmRegister"];
  return path === "noAddress" ? ["cpf", "login", "address"] : ["cpf", "login"];
}

/**
 * The progress dashes, counted as the prototype counts them (`authFlow`): one per step of the
 * whole path, from the CPF to the Ponto de partida. A new person goes through CPF, cadastro,
 * endereço and código; a known one through CPF and código, with endereço when the base has none.
 *
 * The host's line goes on past this wizard into the list creation (evento, termos, ponto de
 * partida, `createListProgress`), so the account steps are counted with those three ahead. A
 * guest has no list, and no list steps. `skipConfirmation` is a known CPF that still owes an
 * address.
 */
export function onboardingProgress({
  step,
  intent,
  skipConfirmation = false,
}: {
  step: OnboardingProgressStep;
  intent: OnboardingIntent;
  skipConfirmation?: boolean;
}): { count: number; current: number } {
  const known = step === "login" || skipConfirmation;
  const flow = accountSteps(known ? (skipConfirmation ? "noAddress" : "complete") : "new");
  const listTail = intent === "host" ? LIST_STEPS.length : 0;

  // The login code that follows a register without auto-login sits where the register code was.
  const position = step === "confirmLogin" ? "confirmRegister" : step;

  return { count: flow.length + listTail, current: Math.max(0, flow.indexOf(position)) };
}

/**
 * The same line on the list creation's screens, which are routes of their own: the account part
 * comes from the path the session recorded, the list part from the screen. A consultant-built
 * list ends at the terms (§2, "Convite da consultora").
 */
export function createListProgress({
  path,
  step,
  invitation = false,
}: {
  /** `session.signupPath`; absent on a session from before it existed. */
  path: SignupPath | undefined;
  step: ListStep;
  /** A list the consultant built: the terms are its last step. */
  invitation?: boolean;
}): { count: number; current: number } {
  const account = accountSteps(path ?? "complete").length;
  const listSteps: readonly ListStep[] = invitation ? ["termos"] : LIST_STEPS;

  return { count: account + listSteps.length, current: account + listSteps.indexOf(step) };
}
